import { useState } from 'react';

interface CreateStoryModalProps {
  onClose: () => void;
}

export function CreateStoryModal({ onClose }: CreateStoryModalProps) {
  const [selectedType, setSelectedType] = useState<'achievement' | 'image'>('achievement');
  const [selectedAchievement, setSelectedAchievement] = useState<string>('');

  const recentAchievements = [
    { id: '1', type: 'badge', title: 'Vendedor do Mês', icon: 'ri-trophy-line', color: 'from-amber-400 to-orange-500' },
    { id: '2', type: 'level', title: 'Nível 12 Alcançado', icon: 'ri-vip-crown-line', color: 'from-purple-400 to-pink-500' },
    { id: '3', type: 'mission', title: 'Missão Completa', icon: 'ri-flag-line', color: 'from-teal-400 to-cyan-500' },
    { id: '4', type: 'badge', title: 'Streak de 7 Dias', icon: 'ri-fire-line', color: 'from-red-400 to-orange-500' }
  ];

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-slate-200 p-6 flex items-center justify-between">
          <h2 className="text-2xl font-black text-slate-800">Criar Story</h2>
          <button
            onClick={onClose}
            className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center hover:bg-slate-200 transition-colors"
          >
            <i className="ri-close-line text-xl text-slate-600"></i>
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Type Selector */}
          <div className="flex gap-3">
            <button
              onClick={() => setSelectedType('achievement')}
              className={`flex-1 p-4 rounded-xl border-2 transition-all ${
                selectedType === 'achievement'
                  ? 'border-teal-500 bg-teal-50'
                  : 'border-slate-200 hover:border-slate-300'
              }`}
            >
              <i className="ri-trophy-line text-3xl mb-2 text-teal-600"></i>
              <p className="font-bold text-slate-800">Conquista</p>
              <p className="text-xs text-slate-500">Compartilhe uma conquista</p>
            </button>
            <button
              onClick={() => setSelectedType('image')}
              className={`flex-1 p-4 rounded-xl border-2 transition-all ${
                selectedType === 'image'
                  ? 'border-teal-500 bg-teal-50'
                  : 'border-slate-200 hover:border-slate-300'
              }`}
            >
              <i className="ri-image-line text-3xl mb-2 text-purple-600"></i>
              <p className="font-bold text-slate-800">Imagem</p>
              <p className="text-xs text-slate-500">Envie uma foto</p>
            </button>
          </div>

          {/* Achievement Selection */}
          {selectedType === 'achievement' && (
            <div className="space-y-3">
              <label className="block text-sm font-bold text-slate-700">
                Selecione uma conquista recente:
              </label>
              <div className="grid grid-cols-2 gap-3">
                {recentAchievements.map((achievement) => (
                  <button
                    key={achievement.id}
                    onClick={() => setSelectedAchievement(achievement.id)}
                    className={`p-4 rounded-xl border-2 transition-all ${
                      selectedAchievement === achievement.id
                        ? 'border-teal-500 bg-teal-50'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <div className={`w-12 h-12 mx-auto mb-2 bg-gradient-to-br ${achievement.color} rounded-xl flex items-center justify-center`}>
                      <i className={`${achievement.icon} text-2xl text-white`}></i>
                    </div>
                    <p className="text-sm font-bold text-slate-800 text-center">{achievement.title}</p>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Image Upload */}
          {selectedType === 'image' && (
            <div className="space-y-3">
              <label className="block text-sm font-bold text-slate-700">
                Envie uma imagem:
              </label>
              <div className="border-2 border-dashed border-slate-300 rounded-xl p-8 text-center hover:border-teal-500 transition-colors cursor-pointer">
                <i className="ri-upload-cloud-line text-5xl text-slate-400 mb-3"></i>
                <p className="text-sm font-medium text-slate-600">Clique para enviar</p>
                <p className="text-xs text-slate-400 mt-1">PNG, JPG até 5MB</p>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              onClick={onClose}
              className="flex-1 px-6 py-3 bg-slate-100 text-slate-700 rounded-xl font-bold hover:bg-slate-200 transition-colors"
            >
              Cancelar
            </button>
            <button
              disabled={selectedType === 'achievement' && !selectedAchievement}
              className="flex-1 px-6 py-3 bg-gradient-to-r from-teal-500 to-cyan-600 text-white rounded-xl font-bold hover:scale-105 transition-transform shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
            >
              Publicar Story
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
